import { defineComponent as _defineComponent } from 'vue'
import store from "@/store";
  import { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
  import { CustomizeHouseStepsEnum } from "@/enums/CustomizeHouseSteps";
  import { computed, ref, onMounted, watch } from "vue";
  import CustomizeHouseSlider from "@/components/builder/CustomizeHouseSlider.vue";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { BuilderSliderSteps } from "@/enums/BuilderSliderSteps";
  import { BuilderFeatures } from "@/enums/BuilderFeatures";
  import { userSelectionDefault } from "@/constants/BuilderUserSelectionDefault";
  import {
    EventActionName,
    EventByAction,
    EventByCategory,
    EventLabelByName,
  } from "@/types/EventTracking";
  import { eventTracking } from "@/helpers/googleAnalyticsEventTracking";
  import BuilderSliderButtons from "./BuilderSliderButtons.vue";
  import { scrollToNextStepMobile } from "@/helpers/builder/scrollToNextStep";
  import { BuilderStepsIds } from "@/enums/BuilderStepsIds";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { scrollTo } from "@/helpers/smoothScrollToElement";
  import HouseFeatures from "@/components/builder/HouseFeatures.vue";
  import BuilderSliderImages from "./BuilderSliderImages.vue";
  import { formatPrice as formatMeters } from "@/helpers/transformation/price";
  import BuilderLightboxImages from "./BuilderLightboxImages.vue";
  import BuilderSelectFacade from "./budget-houses/house-details/BuilderSelectFacade.vue";
  import BuilderEstimatorHouseFeaturesDesktop from "./estimator-tool/builder-estimator-selection-house/house-features/BuilderEstimatorHouseFeaturesDesktop.vue";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import { useToast } from "vue-toastification";
  import { BuilderQuote } from "@/types/BuilderQuote";
  import getUserRole from "@/helpers/getUserRole";
  import ERROR_SOMETHING_WENT_WRONG from "@/constants/errorsMessages";

  interface Props {
    selectedHouseDesign: HouseDesignInterface;
    selectedBuilder?: BuilderInterfaceWithHouseDesigns;
    isBuilderQuoteView?: boolean;
    quote: BuilderQuote | undefined;
    selectedFacade: FacadeInterface | null;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderSelectionHouse',
  props: {
    selectedHouseDesign: {},
    selectedBuilder: {},
    isBuilderQuoteView: { type: Boolean },
    quote: {},
    selectedFacade: {}
  },
  emits: [
    "onButtonStepVisibly",
    "onFeaturesHidden",
    "onFeaturesVisibly",
    "updateSelectedFacade",
  ],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;
  const sliderImages = ref<string[]>([]);
  const currentFeature = ref(BuilderFeatures.DIMENSIONS);
  const emits = __emit;

  const isSliderOpen = ref(false);
  const fadeInEffect = ref(false);
  const isImageFloorplanModalOpen = ref(false);
  const zoom = ref(1);
  const facade = ref<FacadeInterface | null>(null);
  const toast = useToast();

  const selectedBuilder = computed(() => props.selectedBuilder);
  const userRole = getUserRole();

  const onUpdateSelectedFacade = (newFacade: FacadeInterface) => {
    sliderImages.value = [newFacade.url];

    if (newFacade._id !== facade.value?._id) {
      facade.value = newFacade;
      sliderImages.value = [newFacade.url];
      store.commit("facade/updateSelectedFacadeId", newFacade?._id);
      emits("updateSelectedFacade", newFacade);
    }
  };

  const house = computed(() => {
    return props.selectedHouseDesign ?? selectedBuilder.value?.houses[0];
  });
  const { isDesktop } = useIsDesktop(990);
  const isHouseSelected = ref(false);
  const isButtonsInteriors = ref(false);
  const showFloorplan = ref(false);

  const currentSliderStepIndex = ref(0);
  const sliderSteps = ref([...Object.values(BuilderSliderSteps)]);
  const nextStep = computed(
    () => sliderSteps.value[currentSliderStepIndex.value + 1]
  );
  const backStep = computed(
    () => sliderSteps.value[currentSliderStepIndex.value - 1]
  );
  const currentSliderStep = computed(() => {
    return sliderSteps.value[currentSliderStepIndex.value];
  });

  const showSliderArrows = computed(() => {
    const allowedSteps = [BuilderSliderSteps.LIVING];

    return allowedSteps.includes(currentSliderStep.value);
  });

  const getColorsSelection = computed(() => {
    const { kitchens, bathroom } = house.value;

    if (currentSliderStep.value === BuilderSliderSteps.BATHROOM) {
      return bathroom.options;
    }

    if (currentSliderStep.value === BuilderSliderSteps.KITCHEN) {
      return kitchens;
    }

    return [];
  });

  const openFloorplanImageModal = () => {
    isImageFloorplanModalOpen.value = true;
  };

  const closeImageModal = () => {
    isImageFloorplanModalOpen.value = false;
    zoom.value = 1;
  };

  const updateZoom = (value: number) => {
    const newZoom = zoom.value + value;
    const MIN_ZOOM_LEVEL = 1;
    const MAX_ZOOM_LEVEL = 2.6;

    if (newZoom >= MIN_ZOOM_LEVEL && newZoom <= MAX_ZOOM_LEVEL) {
      zoom.value = newZoom;
    }
  };

  const openFacadeSlider = () => {
    isSliderOpen.value = !isSliderOpen.value;

    setTimeout(() => {
      fadeInEffect.value = !fadeInEffect.value;
    }, 50);
  };

  const setCurrentFeature = (feature: BuilderFeatures) => {
    currentFeature.value = feature;
  };

  const onShowButtonsInteriors = () => {
    jumpToStep(BuilderSliderSteps.KITCHEN);
  };

  const onCustomizeNextStep = () => {
    updateUserSelectionState(CustomizeHouseStepsEnum.HOUSE_DESIGN);
  };
  const handleStepChange = (stepChange: number) => {
    currentSliderStepIndex.value += stepChange;
  };

  const onNextStep = () => {
    if (currentSliderStepIndex.value === sliderSteps.value.length - 1) {
      return;
    }

    handleStepChange(1);
  };

  const onBackStep = () => {
    if (currentSliderStepIndex.value === 0) {
      return;
    }

    handleStepChange(-1);
  };

  const resetSteps = () => {
    handleStepChange(-currentSliderStepIndex.value);
  };

  const setSliderImages = () => {
    if (!house.value) {
      return;
    }

    const {
      facades,
      kitchens,
      bathroom,
      loungeRoom,
      mainImages,
    }: HouseDesignInterface = house.value;

    switch (currentSliderStep.value) {
      case BuilderSliderSteps.MAIN_IMAGE:
        sliderImages.value = mainImages ? [mainImages[0]] : [];
        break;
      case BuilderSliderSteps.SELECTION:
        sliderImages.value = getImagesUrls(
          selectedQuote.value.facade?.url,
          facades
        );
        break;
      case BuilderSliderSteps.LIVING:
        sliderImages.value = loungeRoom.urls;
        break;
      case BuilderSliderSteps.KITCHEN:
        sliderImages.value = getImagesUrls(
          selectedQuote.value.kitchen?.url,
          kitchens
        );
        break;
      case BuilderSliderSteps.BATHROOM:
        sliderImages.value = getImagesUrls(
          selectedQuote.value.bathroom?.url,
          bathroom.options
        );
        break;
      default:
        break;
    }
  };
  const getImagesUrls = (
    selectedUrl: string | undefined,
    images: { url: string }[]
  ): string[] => {
    return selectedUrl ? [selectedUrl] : images.map(({ url }) => url);
  };
  const isImageAvailable = (step: BuilderSliderSteps) => {
    switch (step) {
      case BuilderSliderSteps.KITCHEN:
        return house.value.kitchens.some(
          (kitchen) => kitchen.url && kitchen.url.trim() !== ""
        );
      case BuilderSliderSteps.BATHROOM:
        return house.value.bathroom.options.some(
          (option) => option.url && option.url.trim() !== ""
        );
      case BuilderSliderSteps.LIVING:
        return house.value.loungeRoom.urls.some(
          (url) => url && url.trim() !== ""
        );
      default:
        return false;
    }
  };
  const showBrowseInteriorsText = computed(() => {
    return (
      isImageAvailable(BuilderSliderSteps.KITCHEN) ||
      isImageAvailable(BuilderSliderSteps.BATHROOM) ||
      isImageAvailable(BuilderSliderSteps.LIVING)
    );
  });
  const selectedQuote = computed(() => {
    return store.getters["builder/getSelectedSelection"];
  });
  const updateUserSelectionState = (
    step: CustomizeHouseStepsEnum,
    updateParams = {}
  ) => {
    store.commit("builder/updateUserSelection", {
      ...selectedQuote.value,
      ...updateParams,
      currentStep: step,
      builder: {
        id: selectedBuilder.value?._id,
        name: selectedBuilder.value?.name,
        logo: selectedBuilder.value?.logo,
      },
      house: {
        id: house.value?._id,
        name: house.value?.name,
        price: house.value?.basePrice,
      },
    });
  };

  const trackHouseDesignClick = (houseDesign: HouseDesignInterface) => {
    eventTracking({
      actionsName: EventActionName.HOUSE_DESIGN_ON_CLICK,
      eventByCategory: EventByCategory.ON_CLICK,
      eventLabelByName: EventLabelByName.CLICK_HOUSE_DESIGN,
      eventByAction: EventByAction.CLICK,
      houseDesignId: houseDesign._id,
      houseDesignName: houseDesign.name,
    });
  };
  const onSelectColor = (userSelection: {
    color: string;
    name: string;
    url: string;
  }) => {
    sliderImages.value = [userSelection.url];
  };

  const showColorsControls = computed(() => {
    const { BATHROOM, KITCHEN } = BuilderSliderSteps;
    const {
      bathroom: { options },
      kitchens,
    } = house.value;

    const isBathroomStep = currentSliderStep.value === BATHROOM;
    const isKitchenStep = currentSliderStep.value === KITCHEN;

    return (
      (isBathroomStep && options.length > 1) ||
      (isKitchenStep && kitchens.length > 1)
    );
  });
  const showExitInteriorsButton = computed(() => {
    const { KITCHEN, BATHROOM, LIVING } = BuilderSliderSteps;

    return [KITCHEN, BATHROOM, LIVING].includes(currentSliderStep.value);
  });
  const onSelectHouseDesign = (houseDesign: HouseDesignInterface) => {
    if (houseDesign._id === selectedQuote.value.house?.id) {
      return;
    }

    if (!isDesktop.value && !isHouseSelected.value) {
      isHouseSelected.value = true;
    }

    const { _id, name, basePrice, floorplans } = houseDesign;

    const updatedUserSelection = {
      ...userSelectionDefault,
      currentStep: CustomizeHouseStepsEnum.HOUSE_DESIGN,
      id: selectedQuote.value.id,
      lot: selectedQuote.value.lot,
      builder: selectedQuote.value.builder,
      house: { id: _id, name, price: houseDesign.basePrice },
      floorplan: {
        url: floorplans[0].url,
        name: floorplans[0]?.name,
      },
    };

    store.commit("builder/updateUserSelection", updatedUserSelection);
    trackHouseDesignClick(houseDesign);

    emits("onButtonStepVisibly", true);
  };

  const jumpToStep = (step: BuilderSliderSteps) => {
    const stepIndex = sliderSteps.value.indexOf(step);

    if (stepIndex !== -1) {
      currentSliderStepIndex.value = stepIndex;
    }
  };

  const exitInteriors = () => {
    if (showFloorplan.value) {
      showFloorplan.value = false;
    } else {
      jumpToStep(BuilderSliderSteps.MAIN_IMAGE);
    }
  };

  const onChangeImage = () => {
    showFloorplan.value = true;
  };

  const setAsOpened = async () => {
    if (props.quote && !props.quote?.wasOpened && userRole.isCustomer) {
      await store
        .dispatch("builder/setAsOpened", props.quote?._id)
        .then()
        .catch(() => {
          toast.error(ERROR_SOMETHING_WENT_WRONG);
        });
    }
  };

  watch(currentSliderStepIndex, () => {
    setSliderImages();
  });

  watch(props, async () => {
    await setAsOpened();
  });

  const initializeHouseDesign = () => {
    if (props.selectedHouseDesign) {
      setSliderImages();
      resetSteps();
    }
  };

  watch(
    () => props.selectedFacade,
    (newFacade) => {
      if (newFacade) {
        onUpdateSelectedFacade(newFacade);
      }
    }
  );

  watch(
    () => props.selectedHouseDesign,
    (newDesign) => {
      if (newDesign) {
        initializeHouseDesign();
      }
    },
    { immediate: true, deep: true }
  );

  onMounted(() => {
    isHouseSelected.value = false;

    if (
      !props.selectedHouseDesign &&
      selectedBuilder.value &&
      selectedBuilder.value?.houses.length > 0
    ) {
      onSelectHouseDesign(selectedBuilder.value.houses[0]);
    } else {
      initializeHouseDesign();
    }
  });

  watch(
    props,
    () => {
      if (
        !props.selectedHouseDesign &&
        selectedBuilder.value &&
        selectedBuilder.value?.houses.length > 0
      ) {
        onSelectHouseDesign(selectedBuilder.value.houses[0]);
      } else if (!props.isBuilderQuoteView) {
        setSliderImages();
        resetSteps();
      }
    },
    { deep: true, immediate: true }
  );

const __returned__ = { props, sliderImages, currentFeature, emits, isSliderOpen, fadeInEffect, isImageFloorplanModalOpen, zoom, facade, toast, selectedBuilder, userRole, onUpdateSelectedFacade, house, isDesktop, isHouseSelected, isButtonsInteriors, showFloorplan, currentSliderStepIndex, sliderSteps, nextStep, backStep, currentSliderStep, showSliderArrows, getColorsSelection, openFloorplanImageModal, closeImageModal, updateZoom, openFacadeSlider, setCurrentFeature, onShowButtonsInteriors, onCustomizeNextStep, handleStepChange, onNextStep, onBackStep, resetSteps, setSliderImages, getImagesUrls, isImageAvailable, showBrowseInteriorsText, selectedQuote, updateUserSelectionState, trackHouseDesignClick, onSelectColor, showColorsControls, showExitInteriorsButton, onSelectHouseDesign, jumpToStep, exitInteriors, onChangeImage, setAsOpened, initializeHouseDesign, get store() { return store }, get BuilderInterfaceWithHouseDesigns() { return BuilderInterfaceWithHouseDesigns }, get CustomizeHouseStepsEnum() { return CustomizeHouseStepsEnum }, computed, ref, onMounted, watch, CustomizeHouseSlider, get HouseDesignInterface() { return HouseDesignInterface }, get BuilderSliderSteps() { return BuilderSliderSteps }, get BuilderFeatures() { return BuilderFeatures }, get userSelectionDefault() { return userSelectionDefault }, get EventActionName() { return EventActionName }, get EventByAction() { return EventByAction }, get EventByCategory() { return EventByCategory }, get EventLabelByName() { return EventLabelByName }, get eventTracking() { return eventTracking }, BuilderSliderButtons, get scrollToNextStepMobile() { return scrollToNextStepMobile }, get BuilderStepsIds() { return BuilderStepsIds }, get useIsDesktop() { return useIsDesktop }, get scrollTo() { return scrollTo }, HouseFeatures, BuilderSliderImages, get formatMeters() { return formatMeters }, BuilderLightboxImages, BuilderSelectFacade, BuilderEstimatorHouseFeaturesDesktop, get FacadeInterface() { return FacadeInterface }, get useToast() { return useToast }, get BuilderQuote() { return BuilderQuote }, get getUserRole() { return getUserRole }, get ERROR_SOMETHING_WENT_WRONG() { return ERROR_SOMETHING_WENT_WRONG } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})